'use client'

import { motion } from 'motion/react'
import {
  TrendingUp,
  TrendingDown,
  Trophy,
  Target,
  Zap,
  Shield,
  Award,
  Flag,
} from 'lucide-react'
import { SectionLabel } from './player-card'
import { useMk8Player } from '@/hooks/use-mk8-player'

const STATS = [
  {
    label: 'TOTAL WINS',
    value: '863',
    sub: '+12 this week',
    trend: 'up',
    color: '#ffe600',
    icon: Trophy,
    bg: 'rgba(255,230,0,0.06)',
    border: 'rgba(255,230,0,0.15)',
  },
  {
    label: 'WIN RATE',
    value: '67.3%',
    sub: '+2.1% vs last month',
    trend: 'up',
    color: '#00eaff',
    icon: Target,
    bg: 'rgba(0,234,255,0.06)',
    border: 'rgba(0,234,255,0.15)',
  },
  {
    label: 'AVG FINISH',
    value: '2.4',
    sub: 'Out of 12 players',
    trend: 'up',
    color: '#00ff88',
    icon: Flag,
    bg: 'rgba(0,255,136,0.06)',
    border: 'rgba(0,255,136,0.15)',
  },
  {
    label: 'PODIUMS',
    value: '1,102',
    sub: '85.8% top-3 rate',
    trend: 'up',
    color: '#a78bfa',
    icon: Award,
    bg: 'rgba(167,139,250,0.06)',
    border: 'rgba(167,139,250,0.15)',
  },
  {
    label: 'PEAK MMR',
    value: '8,740',
    sub: 'Current season record',
    trend: 'neutral',
    color: '#ff3544',
    icon: Zap,
    bg: 'rgba(255,53,68,0.06)',
    border: 'rgba(255,53,68,0.15)',
  },
  {
    label: 'TOTAL RACES',
    value: '1,284',
    sub: 'Since Jan 2024',
    trend: 'neutral',
    color: '#ff9900',
    icon: Shield,
    bg: 'rgba(255,153,0,0.06)',
    border: 'rgba(255,153,0,0.15)',
  },
]

export function StatsGrid({ profileId }: { profileId?: string }) {
  const { myStats, rank } = useMk8Player(profileId)

  const stats = myStats
    ? [
        {
          label: 'PLAYER MMR',
          value: `${myStats.mmr}`,
          sub: `Current rank #${rank + 1}`,
          trend: 'up',
          color: '#ffe600',
          icon: Trophy,
          bg: 'rgba(255,230,0,0.06)',
          border: 'rgba(255,230,0,0.15)',
        },
        {
          label: 'TOTAL POINTS',
          value: `${myStats.points}`,
          sub: `${myStats.races} races played`,
          trend: 'up',
          color: '#00eaff',
          icon: Target,
          bg: 'rgba(0,234,255,0.06)',
          border: 'rgba(0,234,255,0.15)',
        },
        {
          label: 'AVG POINTS/RACE',
          value: myStats.avg.toFixed(2),
          sub: `Average performance`,
          trend: myStats.avg >= 12 ? 'up' : 'neutral',
          color: '#00ff88',
          icon: Flag,
          bg: 'rgba(0,255,136,0.06)',
          border: 'rgba(0,255,136,0.15)',
        },
        {
          label: 'BEST DRIVER',
          value: `${myStats.bestDriverCount}`,
          sub: `${myStats.bestDriver}`,
          trend: 'up',
          color: '#a78bfa',
          icon: Award,
          bg: 'rgba(167,139,250,0.06)',
          border: 'rgba(167,139,250,0.15)',
        },
        {
          label: 'PEAK MMR',
          value: `${myStats.mmr}`,
          sub: 'Current season',
          trend: 'neutral',
          color: '#ff3544',
          icon: Zap,
          bg: 'rgba(255,53,68,0.06)',
          border: 'rgba(255,53,68,0.15)',
        },
        {
          label: 'TOTAL RACES',
          value: `${myStats.races}`,
          sub: 'Since the beginning',
          trend: 'neutral',
          color: '#ff9900',
          icon: Shield,
          bg: 'rgba(255,153,0,0.06)',
          border: 'rgba(255,153,0,0.15)',
        },
      ]
    : STATS

  return (
    <section
      id="stats"
      className="relative py-24 px-6"
      style={{ background: '#08080e' }}
    >
      {/* Divider line */}
      <div
        className="absolute top-0 left-6 right-6 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(0,234,255,0.15), transparent)',
        }}
      />

      <div className="max-w-5xl mx-auto">
        <SectionLabel label="PERFORMANCE STATS" />

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {stats.map(
            (
              { label, value, sub, trend, color, icon: Icon, bg, border },
              i,
            ) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  delay: i * 0.07,
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ scale: 1.025, y: -2 }}
                className="relative rounded-2xl p-5 overflow-hidden cursor-default"
                style={{ background: bg, border: `1px solid ${border}` }}
              >
                {/* Glow corner */}
                <div
                  className="absolute top-0 right-0 w-24 h-24 opacity-20"
                  style={{
                    background: `radial-gradient(circle at 100% 0%, ${color}, transparent 60%)`,
                  }}
                />

                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{
                      background: `${color}20`,
                      border: `1px solid ${color}30`,
                    }}
                  >
                    <Icon size={16} color={color} />
                  </div>
                  {trend !== 'neutral' && (
                    <div
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                      style={{
                        background:
                          trend === 'up'
                            ? 'rgba(0,255,136,0.1)'
                            : 'rgba(255,53,68,0.1)',
                      }}
                    >
                      {trend === 'up' ? (
                        <TrendingUp size={11} color="#00ff88" />
                      ) : (
                        <TrendingDown size={11} color="#ff3544" />
                      )}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    fontFamily: "'Russo One', sans-serif",
                    fontSize: '28px',
                    color: '#fff',
                    lineHeight: 1,
                    letterSpacing: '-0.01em',
                  }}
                  className="mb-1"
                >
                  {value}
                </div>

                <p
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    letterSpacing: '0.15em',
                    color: color,
                    fontSize: '10px',
                  }}
                  className="mb-1"
                >
                  {label}
                </p>

                <p
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.35)',
                  }}
                >
                  {sub}
                </p>
              </motion.div>
            ),
          )}
        </div>
      </div>
    </section>
  )
}
